import { matchesSearch } from '@webbuilder/shared-types'
import { useNewItems } from '../../../lib/sectionNewItems'
import { NewItemDot } from '../NewItemDot'
import type { ReactNode } from 'react'
/**
 * Las citas de un servicio.
 *
 * Vive dentro de la ficha de un servicio reservable: la pregunta que responde
 * es «quién viene a esto», no «qué vendo». La lista de servicios es
 * `BookingServicesPanel`, y la ficha de una cita suelta es `BookingDetail`.
 *
 * Es la misma tabla que Ofertas y Contenido, no una lista propia: una cita se
 * mira igual que cualquier otra fila —de quién es, cuándo, en qué estado— y
 * tener dos formas de leer lo mismo obliga a aprenderlas por separado.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import {
  IconAdjustmentsHorizontal,
  IconCalendarEvent,
  IconCalendarPlus,
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconDots,
  IconDownload,
  IconMail,
  IconPhone,
  IconChevronsRight,
  IconX,
  IconClock,
  IconEye,
  IconLayoutGrid,
  IconPlus,
  IconSearch,
  IconUser,
  IconUsers,
} from '@tabler/icons-react'
import { Pagination, usePaged } from '../Pagination'
import type { Translate } from '@webbuilder/i18n'
import { activeLanguage, useI18n } from '../../../lib/i18n'
import { BookingOverdueMark } from './BookingOverdueMark'
import { BookingStatusSelect } from './BookingStatusSelect'
import { DateRangePicker, shiftDay, todayIn, type RangeShortcut } from '../DateRangePicker'
import type { Booking, BookingResource, Offer } from '../../../lib/api'
import { downloadCsv } from '../../../lib/csv'
import {
  BOOKING_STATUS_KEY,
  BOOKING_STATUS_TONE,
  durationLabel,
  startTimeLabel,
  groupBookings,
  longDurationLabel,
  peopleLabel,
  type BookingEntry,
  longDateIn,
  needsOutcome,
  shortDateIn,
  timeIn,
} from './bookingShared'
import { useSearchPlaceholder } from '../../../hooks/useSearchPlaceholder'
import { openOnRowClick } from '../rowOpen'

type StatusFilter = 'all' | 'upcoming' | 'confirmed' | 'held' | 'cancelled'
type BookingSort = 'startSoon' | 'startLate' | 'nameAsc' | 'nameDesc'

const PAGE_SIZE = 12

const STATUS_FILTERS: [StatusFilter, string, typeof IconLayoutGrid][] = [
  ['all', 'bookings.filter.all', IconLayoutGrid],
  ['upcoming', 'bookings.filter.upcoming', IconClock],
  ['confirmed', 'bookings.filter.confirmed', IconCheck],
  ['held', 'bookings.filter.held', IconClock],
  ['cancelled', 'bookings.filter.cancelled', IconX],
]

/**
 * «4 asistieron», «1 cancelada»: el desglose de una clase.
 *
 * Antes se derivaba del estado bajando a minúscula y pegando una «s». Eso es
 * gramática del castellano cableada en una función: en inglés «1 cancelleds» no
 * existe, y ni siquiera en castellano funcionaba —«asistió» no hace «asistiós»—,
 * de ahí los dos casos especiales que había. Ahora cada forma es una clave.
 */
function statusCountLabel(status: Booking['status'], count: number, t: Translate): string {
  return t(`bookings.tally.${status}.${count === 1 ? 'one' : 'many'}`, { count })
}

/** `30 de Jun`, para el bloque de fecha de la fila móvil: sin el nombre del día, sólo la fecha. */
function mobileDateIn(iso: string, timeZone: string): string {
  const tag = activeLanguage()
  const options: Intl.DateTimeFormatOptions = { timeZone, day: 'numeric', month: 'short' }
  const date = new Date(iso)
  /*
   * En español se arma a mano porque `Intl` da «4 sept» y aquí el mes va con
   * inicial alta y su « de »; en cualquier otro idioma se deja formatear
   * a `Intl` y punto.
   *
   * Antes no había esa bifurcación: se pedían las piezas en el idioma vigente y
   * se pegaban SIEMPRE con « de » y una mayúscula de `es-MX`. En inglés eso
   * daba «Friday, 4 de September» —tres palabras inglesas unidas por una
   * preposición española—, que es el error clásico de construir una fecha
   * juntando trozos. Es la misma guarda que ya usan los ayudantes de `dates.ts`.
   */
  if (!tag.startsWith('es')) return new Intl.DateTimeFormat(tag, options).format(date)
  const parts = new Intl.DateTimeFormat(tag, options).formatToParts(date)
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
  const month = value('month')
  return `${value('day')} de ${month.charAt(0).toLocaleUpperCase(tag)}${month.slice(1)}`
}

/**
 * La ficha conjunta de una reservación grupal.
 *
 * La fila conserva el resumen que sirve para recorrer la agenda; la lista de
 * asistentes aparece aquí, con espacio para leer nombres, estados y abrir una
 * ficha individual sin convertir la tabla principal en una tabla dentro de otra.
 */
export function GroupBookingDialog({ entry, image, onClose, onOpen, busyId, onStatusChange }: {
  entry: Extract<BookingEntry, { kind: 'group' }>
  /**
   * La portada del servicio, en 16:9.
   *
   * Llega hecha y no se busca aquí: quien abre la ficha ya tiene el catálogo
   * cargado, y pedirlo desde dentro sería una consulta por cada vez que se abre
   * un grupo. Sin ella queda el icono, que es lo que hay en un servicio sin foto.
   */
  image?: string | undefined
  onClose: () => void
  onOpen: (bookingId: string) => void
  busyId: string | null
  onStatusChange?: (booking: Booking, status: Booking['status']) => void | Promise<void>
}) {
  const { t } = useI18n()
  const { lead, members } = entry
  const backdropRef = useRef<HTMLDivElement>(null)
  const groupBodyRef = useRef<HTMLDivElement>(null)
  const [closing, setClosing] = useState(false)
  const tally = (Object.keys(BOOKING_STATUS_KEY) as Booking['status'][])
    .map((status) => ({ status, count: members.filter((row) => row.status === status).length }))
    .filter((row) => row.count > 0)

  const requestClose = useCallback(() => {
    if (busyId || closing) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onClose()
      return
    }
    setClosing(true)
  }, [busyId, closing, onClose])

  useEffect(() => {
    if (!closing) return
    // La salida usa el mismo plazo corto de las tarjetas móviles del CMS.
    const timer = window.setTimeout(onClose, 240)
    return () => window.clearTimeout(timer)
  }, [closing, onClose])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || busyId) return
      const backdrops = document.querySelectorAll('.dash-modal-backdrop')
      if (backdrops.item(backdrops.length - 1) === backdropRef.current) requestClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [busyId, requestClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={backdropRef}
      className={`dash-modal-backdrop dash-booking-group-backdrop${closing ? ' is-closing' : ''}`}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget && !busyId) requestClose()
      }}
    >
      <section
        className={`dash-booking-group-dialog${closing ? ' is-closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dash-booking-group-title"
        onWheel={(event) => {
          if (groupBodyRef.current && !groupBodyRef.current.contains(event.target as Node)) {
            groupBodyRef.current.scrollTop += event.deltaY
          }
        }}
      >
        {/* La misma barra que la ficha de una cita: cerrar a la izquierda. */}
        <div className="dash-bk-bar">
          <button type="button" className="dash-bk-bar-btn" aria-label={t('bookings.group.closePanel')} data-tooltip={t('bookings.group.closePanel')} onClick={requestClose}>
            <IconChevronsRight size={17} />
          </button>
        </div>
        <header className="dash-booking-group-dialog-head">
          {image
            ? <img className="dash-booking-group-dialog-shot" src={image} alt="" />
            : (
              <span className="dash-widget-glass-icon dash-booking-group-dialog-icon" aria-hidden="true">
                <IconUsers size={20} />
              </span>
            )}
          <div>
            <span className="dash-bk-eyebrow">{t('bookings.group.eyebrow')}</span>
            <h2 id="dash-booking-group-title">{lead.offerTitle || t('bookings.group.fallbackTitle')}</h2>
            <p>
              <IconCalendarEvent size={14} aria-hidden="true" />
              <span>{longDateIn(lead.startsAt, lead.timezone)}</span>
            </p>
          </div>
        </header>

        <div className="dash-booking-group-summary">
          <dl>
            <div>
              <dt>{t('bookings.group.reservations')}</dt>
              <dd>{peopleLabel(members.length, t)}</dd>
            </div>
            <div>
              <dt>{t('bookings.group.schedule')}</dt>
              <dd>{t('bookings.group.scheduleRange', { from: timeIn(lead.startsAt, lead.timezone), to: timeIn(lead.endsAt, lead.timezone) })}</dd>
            </div>
            <div>
              <dt>{t('bookings.group.duration')}</dt>
              <dd>{longDurationLabel(lead, t)}</dd>
            </div>
          </dl>
          <div className="dash-booking-group-status-summary">
            <span>{t('bookings.group.statusTitle')}</span>
            <div className="dash-booking-group-statuses" aria-label={t('bookings.group.statusAria')}>
              {tally.map(({ status, count }) => (
                <span className={`dash-booking-status ${BOOKING_STATUS_TONE[status]}`} key={status}>
                  {statusCountLabel(status, count, t)}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="dash-booking-group-dialog-body" ref={groupBodyRef}>
          {/* Sin encabezado: la cabecera de arriba ya dice de qué clase es y
              cuántas personas hay, y «Gestiona las reservaciones» sobre una lista
              de reservaciones no añade nada. */}
          <ol className="dash-booking-group-attendees">
            {members.map((member) => (
              <li key={member.id}>
                <span className="dash-widget-glass-icon dash-booking-group-seat" aria-hidden="true"><IconUser size={16} /></span>
                <span className="dash-booking-group-who">
                  <strong>{member.customerName || t('bookings.noName')}<BookingOverdueMark booking={member} /></strong>
                  <small>{member.customerEmail || t('bookings.noEmail')}</small>
                  <em>{(member.partySize ?? 1) > 1
                    ? t('bookings.group.seats', { from: member.seatIndex + 1, to: member.seatIndex + (member.partySize ?? 1) })
                    : t('bookings.group.seat', { seat: member.seatIndex + 1 })}</em>
                </span>
                <span className="dash-booking-group-attendee-status">
                  {onStatusChange ? (
                    <BookingStatusSelect
                      booking={member}
                      busy={busyId === member.id}
                      portal
                      onChange={(status) => { void onStatusChange(member, status) }}
                    />
                  ) : (
                    <span className={`dash-booking-status ${BOOKING_STATUS_TONE[member.status]}`}>
                      {t(BOOKING_STATUS_KEY[member.status])}
                    </span>
                  )}
                </span>
                <button
                  className="dash-secondary-btn dash-booking-group-view"
                  type="button"
                  onClick={() => onOpen(member.id)}
                >
                  <IconEye size={13} /> {t('bookings.group.viewDetail')}
                </button>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </div>,
    document.body,
  )
}

/** La fila resume el grupo; pulsarla abre su ficha conjunta. */
function GroupRow({ entry, image, hideService, open, newArrival, onToggle, onOpen, onOpenService, busyId, onStatusChange }: {
  entry: Extract<BookingEntry, { kind: 'group' }>
  /** La portada del servicio, para la ficha del grupo. */
  image?: string | undefined
  hideService: boolean
  open: boolean
  newArrival: boolean
  onToggle: () => void
  onOpen: (bookingId: string) => void
  onOpenService?: ((offerId: string) => void) | undefined
  busyId: string | null
  onStatusChange?: (booking: Booking, status: Booking['status']) => void | Promise<void>
}) {
  const { t } = useI18n()
  const { lead, members } = entry
  /* «4 confirmadas · 1 cancelada», en el orden de siempre y sin los vacíos. */
  const tally = (Object.keys(BOOKING_STATUS_KEY) as Booking['status'][])
    .map((status) => ({ status, count: members.filter((row) => row.status === status).length }))
    .filter((row) => row.count > 0)

  return (
    <article className="dash-project-row dash-data-row dash-booking-group" onClick={openOnRowClick(() => onToggle())}>
      <div
        className="dash-project-row-main"
        role="button"
        tabIndex={0}
        onKeyDown={(event) => { if (event.key === 'Enter') onToggle() }}
      >
        <span className="dash-project-row-thumb dash-page-project-thumb dash-row-glass-fallback">
          <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog" aria-hidden="true">
            <IconUsers size={13} />
          </span>
        </span>
        <span className="dash-project-row-copy">
          <span className="dash-project-row-title">
            {newArrival ? <NewItemDot /> : null}
            <span
              className="dash-offer-kind-icon"
              role="img"
              aria-label={t('offers.kind.booking')}
              title={t('offers.kind.booking')}
            >
              <IconCalendarEvent size={14} aria-hidden="true" />
            </span>
            <strong>{lead.offerTitle || t('bookings.group.fallbackTitle')}</strong>
          </span>
          {/* La cuenta baja al segundo renglón, donde va el correo en una cita
              suelta. Al lado del título le robaba el ancho y el nombre del
              servicio se cortaba justo cuando más falta hace leerlo entero. */}
          <span className="dash-booking-group-count">{peopleLabel(members.length, t)}</span>
        </span>
      </div>

      <div className="dash-list-row-meta dash-booking-row-meta">
        {hideService ? null : (
          <span className="dash-data-cell dash-booking-service-cell" data-label={t('bookings.col.service')}>
            {lead.offerId && onOpenService
              ? <button type="button" className="dash-booking-service-link" onClick={() => onOpenService(lead.offerId as string)}>{lead.offerTitle || t('bookings.serviceFallback')}</button>
              : lead.offerTitle || <em>{t('bookings.noService')}</em>}
          </span>
        )}
        {/* En una clase basta con que a UNA persona le falte el resultado: la
            fila entera es lo que hay que abrir para arreglarlo. */}
        <span className="dash-data-cell dash-booking-date-cell" data-label={t('bookings.col.date')}>
          <BookingOverdueMark booking={members.find(needsOutcome) ?? lead} />
          <span className="dash-booking-date-compact">{shortDateIn(lead.startsAt, lead.timezone)}</span>
          <span className="dash-booking-date-mobile">{mobileDateIn(lead.startsAt, lead.timezone)}</span>
        </span>
        <span className="dash-data-cell dash-booking-time-cell" data-label={t('bookings.col.time')}>
          <IconClock className="dash-booking-time-icon" size={14} aria-hidden="true" />
          {timeIn(lead.startsAt, lead.timezone)}
        </span>
        <span className="dash-data-cell dash-booking-duration-cell" data-label={t('bookings.col.duration')}>{durationLabel(lead, t)}</span>
        <span className="dash-data-cell dash-booking-status-cell" data-label={t('bookings.col.status')}>
          {/* Y en Estado, el desglose: «4 asistieron», «3 confirmadas» y debajo
              «1 cancelada». Repetir aquí la cuenta que ya está a la izquierda
              gastaba la única columna que puede decir CÓMO acabó la clase.

              Uno por renglón y no unidos con «·»: la cadena junta no cabía en la
              columna y, como las celdas van en `nowrap`, no se partía sino que
              se salía por encima del botón. Apilados, cada desenlace ocupa su
              línea y la altura crece sola cuando hay dos, que es lo que tiene
              que pasar. */}
          <span className="dash-booking-group-tally">
            {tally.map((row) => (
              <span key={row.status}>{statusCountLabel(row.status, row.count, t)}</span>
            ))}
          </span>
        </span>
      </div>

      <div className="dash-project-row-actions">
        <button
          className="dash-secondary-btn dash-project-row-action"
          type="button"
          onClick={onToggle}
          aria-label={t('bookings.group.view')}
          data-tooltip={t('bookings.group.view')}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <IconEye size={14} />
          <span className="dash-project-row-action-label">{t('bookings.group.view')}</span>
        </button>
      </div>

      {open ? (
        <GroupBookingDialog
          {...(image ? { image } : {})}
          entry={entry}
          onClose={onToggle}
          onOpen={onOpen}
          busyId={busyId}
          {...(onStatusChange ? { onStatusChange } : {})}
        />
      ) : null}
    </article>
  )
}

/** La cara de un artista, en miniatura: su foto o sus iniciales. */
export function ArtistFace({ name, photoUrl }: { name: string; photoUrl: string | null }) {
  return (
    <span className="dash-booking-artist-face" aria-hidden="true">
      {photoUrl ? <img src={photoUrl} alt="" /> : name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase()}
    </span>
  )
}

export function BookingsTable({
  bookings,
  loading,
  failed,
  failureReason,
  refreshControl, onReload,
  onOpen,
  onOpenService,
  onCreate,
  onCreateService,
  range,
  onRangeChange,
  rangeOn = false,
  onRangeClear,
  services,
  serviceFilter = 'all',
  resources = [],
  onServiceFilterChange,
  hideService = false,
  onStatusChange,
}: {
  bookings: Booking[]
  loading: boolean
  failed: boolean
  failureReason: string
  refreshControl?: ReactNode
  onReload: () => void
  onOpen: (bookingId: string) => void
  /** Abre la ficha del servicio desde su columna, sin abrir la cita. */
  onOpenService?: ((offerId: string) => void) | undefined
  onCreate?: (() => void) | undefined
  /**
   * Crear el primer servicio con agenda.
   *
   * Sin servicios no hay citas posibles, así que el vacío de la lista deja de
   * ofrecer «apuntar una cita» —que no se puede— y ofrece el paso que de verdad
   * falta. Es el mismo camino que el calendario, y por eso el mismo manejador.
   * Ausente dentro de la ficha de un servicio: allí el servicio ya existe.
   */
  onCreateService?: (() => void) | undefined
  /**
   * El rango de fechas. Opcional porque dentro de la ficha de un servicio las
   * citas ya vienen acotadas por quien llama, y ofrecer ahí un segundo rango
   * daría dos mandos para lo mismo.
   */
  range?: { from: string; to: string } | undefined
  onRangeChange?: ((range: { from: string; to: string }) => void) | undefined
  /**
   * Si el rango lo eligió alguien y no es el de fábrica. Cuenta como filtro:
   * unas fechas sin citas vacían la lista igual que un estado sin citas.
   */
  rangeOn?: boolean | undefined
  /** Vuelve al rango de fábrica. */
  onRangeClear?: (() => void) | undefined
  /**
   * Los servicios entre los que filtrar. Opcional, y ésa es la diferencia entre
   * las dos pantallas que usan esta tabla: en la ficha de un servicio las citas
   * ya son suyas, y un filtro con una sola opción es un mando que no hace nada.
   */
  services?: Offer[] | undefined
  /** El equipo: para decir con quién es cada cita y filtrar por artista. */
  resources?: BookingResource[] | undefined
  serviceFilter?: string | undefined
  onServiceFilterChange?: ((offerId: string) => void) | undefined
  /**
   * Sin la columna del servicio.
   *
   * Dentro de la ficha de un servicio todas las citas son suyas, así que esa
   * columna repite el mismo texto en cada fila. Y quitarla no es sólo limpieza:
   * son 170 píxeles que la tabla deja de necesitar, y de eso depende que quepa
   * como tabla en vez de rendirse a la forma de ficha.
   */
  hideService?: boolean | undefined
  /**
   * Cambia el estado sin salir de la lista. Ausente = la insignia es muda.
   *
   * Lo escribe quien llama y no esta tabla: es ella quien tiene el proyecto, el
   * token y la copia de las citas que hay que refrescar después.
   */
  onStatusChange?: ((booking: Booking, status: Booking['status']) => Promise<void>) | undefined
}) {
  const { t } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('bookings.searchPlaceholder')
  const isNewBooking = useNewItems('bookings', bookings, !loading && !failed)
  const [query, setQuery] = useState('')
  /** Qué fila está guardando, para no dejar pulsar dos veces el mismo estado. */
  const [busyId, setBusyId] = useState<string | null>(null)
  /**
   * El menú de los tres puntos: qué fila lo tiene abierto y dónde se pinta.
   *
   * Las mismas acciones que ofrece la ficha de la cita, sin abrirla — escribir,
   * llamar, copiar el correo, ir al servicio—; ninguna es nueva ni hace nada que
   * no se pudiera hacer ya, sólo se alcanzan desde la lista. Era la única de las
   * nueve listas sin esta esquina, y por eso también la única que en el teléfono
   * tenía que dibujar una punta de flecha en su lugar.
   */
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [listMenu, setListMenu] = useState({ top: 0, left: 0, up: false, maxHeight: 0 })
  /** Para el «Copiado» de un instante, con la fila a la que pertenece. */
  const [copiedId, setCopiedId] = useState<string | null>(null)
  /* Sigue visible mientras se despide, aunque los datos ya hayan llegado. */
  const bookingsLoader = useRecordLoader(loading)

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  /* Con varias personas en el equipo: con quién es cada cita, y el filtro por artista. */
  const team = resources.length > 1 ? resources : []
  const [artistFilter, setArtistFilter] = useState('all')
  const [sort, setSort] = useState<BookingSort>('startSoon')
  const [filterOpen, setFilterOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const filterRef = useRef<HTMLDivElement>(null)

  // Cerrar el desplegable al pulsar fuera o con Escape, igual que en Ofertas.
  useEffect(() => {
    if (!filterOpen) return
    const close = (event: PointerEvent) => {
      if (!filterRef.current?.contains(event.target as Node)) setFilterOpen(false)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setFilterOpen(false) }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [filterOpen])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const now = Date.now()

    const filtered = bookings.filter((booking) => {
      if (statusFilter === 'upcoming') {
        if (Date.parse(booking.startsAt) < now) return false
        if (booking.status !== 'confirmed' && booking.status !== 'held') return false
      } else if (statusFilter === 'cancelled') {
        if (booking.status !== 'cancelled' && booking.status !== 'no_show') return false
      } else if (statusFilter !== 'all' && booking.status !== statusFilter) {
        return false
      }
      if (artistFilter !== 'all' && booking.resourceId !== artistFilter) return false
      if (!needle) return true
      return (
        matchesSearch(booking.customerName, needle) ||
        matchesSearch(booking.customerEmail, needle) ||
        matchesSearch(booking.customerPhone, needle) ||
        matchesSearch(booking.offerTitle, needle)
      )
    })

    const byName = (a: Booking, b: Booking) =>
      (a.customerName || 'zzz').localeCompare(b.customerName || 'zzz', 'es')
    const byStart = (a: Booking, b: Booking) => Date.parse(a.startsAt) - Date.parse(b.startsAt)

    /* Se fue el orden por fecha de creación con su columna: era lo único que lo
       ofrecía, y un orden que no se puede pedir desde ningún sitio es código que
       nadie ejecuta. */
    const sorted = [...filtered]
    if (sort === 'startLate') sorted.sort((a, b) => byStart(b, a))
    else if (sort === 'nameAsc') sorted.sort(byName)
    else if (sort === 'nameDesc') sorted.sort((a, b) => byName(b, a))
    else sorted.sort(byStart)
    return sorted
  }, [bookings, query, statusFilter, artistFilter, sort])

  /* Se agrupa DESPUÉS de filtrar y ordenar, y ANTES de paginar: al revés, una
     clase de seis se comería media página y el contador diría veinte cuando en
     pantalla hay ocho renglones. */
  /**
   * Lo que se está viendo, a CSV.
   *
   * Sin agrupar: una fila por cita, aunque en pantalla las de una clase se vean
   * juntas. Lo que se exporta se abre en una hoja para contar o pasar lista, y
   * ahí un grupo de seis tiene que ser seis renglones.
   *
   * La hora, en la zona en que se reservó y también en ISO: la primera es la
   * que se lee, la segunda la que ordena y resta bien en cualquier hoja.
   */
  const exportCsv = () => {
    downloadCsv(
      t('bookings.csv.filename'),
      [
        t('bookings.csv.day'),
        t('bookings.csv.time'),
        t('bookings.csv.startIso'),
        t('bookings.csv.service'),
        t('bookings.csv.customer'),
        t('bookings.csv.email'),
        t('bookings.csv.phone'),
        t('bookings.csv.status'),
        t('bookings.csv.note'),
        t('bookings.csv.answers'),
        t('bookings.csv.createdAt'),
      ],
      visible.map((booking) => [
        shortDateIn(booking.startsAt, booking.timezone),
        `${timeIn(booking.startsAt, booking.timezone)}–${timeIn(booking.endsAt, booking.timezone)}`,
        booking.startsAt,
        booking.offerTitle,
        booking.customerName,
        booking.customerEmail,
        booking.customerPhone,
        t(BOOKING_STATUS_KEY[booking.status]),
        booking.customerNote,
        booking.customFields.map((field) => `${field.label}: ${field.value}`).join(' · '),
        booking.createdAt,
      ]),
    )
  }

  const entries = useMemo(() => groupBookings(visible), [visible])
  /* Una sola ficha grupal a la vez. La clave sobrevive a reordenar la tabla. */
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const paged = usePaged(entries, PAGE_SIZE, `${query}|${statusFilter}|${sort}`)
  /* El servicio cuenta: ahora vive dentro de Filtros, y sin el punto nadie sabría
     que la lista está acotada a uno. */
  const filtersOn = statusFilter !== 'all' || serviceFilter !== 'all' || artistFilter !== 'all'

  /* Con la lista vacía no sale la barra entera: ni buscador, ni filtros, ni
     el botón de crear. No hay nada que buscar ni que acotar, y crear ya lo
     ofrece el propio mensaje de empezar —que es lo único que tiene que
     haber en pantalla. Vuelve con la primera fila, o mientras quede algo
     escrito: si no, una búsqueda sin resultados se llevaría por delante la
     caja donde estaba escrita y no habría cómo deshacerla. */
  /* Y mientras haya un filtro puesto: el servicio se filtra antes de llegar
     aquí, así que uno sin citas —un borrador, por ejemplo— dejaba la lista
     vacía, se llevaba la barra por delante y con ella el único sitio donde se
     podía quitar el filtro. */
  /* Y con unas fechas elegidas, por lo mismo: el rango se pide al servidor, así
     que unas fechas sin citas llegan como una lista vacía, y la barra se iba
     con el selector de fechas —el único sitio donde se podía cambiar—. */
  const showToolbar = bookings.length > 0 || query.trim() !== '' || filtersOn || rangeOn

  /** Alterna el sentido de una columna, o entra en ella por su lado natural. */
  const changeStatus = async (booking: Booking, status: Booking['status']) => {
    if (!onStatusChange || busyId) return
    setBusyId(booking.id)
    try {
      await onStatusChange(booking, status)
    } finally {
      setBusyId(null)
    }
  }

  const toggleSort = (column: 'start' | 'name') => {
    setSort((current) => {
      if (column === 'start') return current === 'startSoon' ? 'startLate' : 'startSoon'
      return current === 'nameAsc' ? 'nameDesc' : 'nameAsc'
    })
  }

  /* La espera sustituye a la sección ENTERA: sin cabecera, sin barra y sin
     tabla, porque una pantalla a medio pintar mueve sus piezas cada vez que
     llega una. Ver `SectionLoader`. */
  if (bookingsLoader.visible) return <SectionLoader loader={bookingsLoader} label={t('bookings.loading')} />

  /* Fragmento y no un `div`: la barra y la tabla son hermanas dentro de la
     rejilla vertical de la pantalla —`.dash-projects-view`, con `gap: 14px`— y
     envueltas en una caja pasan a ser UN solo elemento de esa rejilla. El hueco
     entre ellas desaparecía y el buscador quedaba pegado al contenido.

     La animación de entrada la lleva la raíz de la pantalla, que es quien se
     monta al terminar la espera. */
  /*
   * El rango de fechas, con el mismo selector que Analíticas › Visitas.
   *
   * Eran dos campos de fecha nativos con una flecha en medio: a tamaño de
   * sistema, distintos en cada navegador y sin atajos —«esta semana» costaba
   * abrir dos calendarios—. El de Analíticas ya tenía los dos meses a la vista
   * y la columna de atajos. Aquí mira hacia delante (`allowFuture`), y sus
   * atajos traen las fechas puestas porque no hay servidor de Analíticas que
   * los resuelva. Es un solo botón, así que cabe en la barra también en el
   * teléfono y ya no hace falta mudarlo al desplegable de filtros.
   */
  /* Hoy en la zona de quien mira, que es en la que se leen las horas de la lista. */
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const today = todayIn(browserTimezone)
  const monthEnd = (day: string) => new Date(Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)), 0)).toISOString().slice(0, 10)
  const comingMonthStart = shiftDay(monthEnd(today), 1)
  const rangeShortcuts: RangeShortcut[] = [
    { labelKey: 'range.today', from: today, to: today },
    { labelKey: 'range.next7', from: today, to: shiftDay(today, 6) },
    { labelKey: 'range.next30', from: today, to: shiftDay(today, 29) },
    { labelKey: 'range.thisMonth', from: `${today.slice(0, 8)}01`, to: monthEnd(today) },
    { labelKey: 'range.comingMonth', from: comingMonthStart, to: monthEnd(comingMonthStart) },
    { labelKey: 'range.last30', from: shiftDay(today, -29), to: today },
  ]
  const rangePicker = range && onRangeChange ? (
    <DateRangePicker
      query={{ range: 'custom', from: range.from, to: range.to }}
      timezone={browserTimezone}
      allowFuture
      shortcuts={rangeShortcuts}
      onChange={(next) => { if (next.from && next.to) onRangeChange({ from: next.from, to: next.to }) }}
    />
  ) : null

  /*
   * El servicio, dentro de Filtros y no suelto en la barra.
   *
   * Suelto era un tercer mando junto al buscador, con el mismo peso que él, para
   * algo que se elige una vez y se deja. Dentro del desplegable está al lado del
   * otro filtro de la lista —el estado—, que es lo que es. Como botones y no
   * como `select`: el mismo gesto que el estado justo encima, con desplazamiento
   * propio cuando el negocio tiene muchos servicios.
   */
  const serviceOptions = services && services.length > 1 && onServiceFilterChange ? (
    <div className="dash-filter-section">
      <span className="dash-filter-section-title">{t('bookings.filterByService')}</span>
      <div className="dash-filter-options is-scroll">
        {[{ id: 'all', title: t('bookings.allServices') }, ...services.map((offer) => ({ id: offer.id, title: offer.title }))].map((option) => (
          <button
            key={option.id}
            className={serviceFilter === option.id ? 'active' : ''}
            type="button"
            onClick={() => onServiceFilterChange(option.id)}
          >
            <span>{option.title}</span>
            {serviceFilter === option.id ? <IconCheck size={14} /> : null}
          </button>
        ))}
      </div>
    </div>
  ) : null

  /* El artista, en Filtros junto al estado y el servicio. */
  const artistOptions = team.length ? (
    <div className="dash-filter-section">
      <span className="dash-filter-section-title">{t('bookings.filterByArtist')}</span>
      <div className="dash-filter-options is-scroll">
        {[{ id: 'all', name: t('bookings.allArtists'), photoUrl: null as string | null | undefined }, ...team.map((member) => ({ id: member.id, name: member.name, photoUrl: member.photoUrl }))].map((option) => (
          <button key={option.id} className={artistFilter === option.id ? 'active' : ''} type="button" onClick={() => setArtistFilter(option.id)}>
            {option.id === 'all' ? <IconUsers size={14} /> : <ArtistFace name={option.name} photoUrl={option.photoUrl ?? null} />}
            <span>{option.name}</span>
            {artistFilter === option.id ? <IconCheck size={14} /> : null}
          </button>
        ))}
      </div>
    </div>
  ) : null
  const artistOf = (booking: Booking) => team.find((member) => member.id === booking.resourceId)

  return (
    <>
      {!showToolbar && refreshControl ? <div className="dash-toolbar-lead">{refreshControl}</div> : null}
      {showToolbar ? (
        <header className="dash-toolbar dash-layered-toolbar">
          <div className="dash-toolbar-lead">
            {refreshControl}
          <div className="dash-toolbar-search">
            <button type="button" className="dash-toolbar-search-icon" aria-label={t('bookings.searchAria')} onClick={() => searchRef.current?.focus()}>
              <IconSearch size={16} />
            </button>
            <input
              ref={searchRef}
              type="search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Escape') { setQuery(''); searchRef.current?.blur() } }}
            />
            {query ? (
              <button type="button" className="dash-toolbar-search-clear" aria-label={t('bookings.clearSearch')} onClick={() => { setQuery(''); searchRef.current?.focus() }}>
                <IconX size={13} />
              </button>
            ) : null}
          </div>

          </div>

          <div className="dash-toolbar-controls">
            {/* El rango vive en la barra: no es un filtro sobre lo que ya hay,
                decide qué se le pide al servidor. */}
            {rangePicker}

            {/* Exportar lo que se ve, al lado de los filtros que lo acotaron. */}
            <button
              className="dash-filter-trigger"
              type="button"
              data-tooltip={t('bookings.exportCsv')}
              aria-label={t('bookings.exportCsv')}
              disabled={visible.length === 0}
              onClick={exportCsv}
            >
              <IconDownload size={15} aria-hidden="true" />
            </button>

            <div className="dash-filter-wrap" ref={filterRef}>
              <button
                className="dash-filter-trigger"
                type="button"
                aria-label={t('bookings.filtersAria')}
                data-tooltip={t('bookings.filtersAria')}
                aria-expanded={filterOpen}
                onClick={() => setFilterOpen((open) => !open)}
              >
                <IconAdjustmentsHorizontal size={15} aria-hidden="true" />
                {filtersOn ? <i className="dash-filter-active-dot" aria-hidden="true" /> : null}
              </button>
              {filterOpen ? (
                <div className="dash-filter-popover" role="dialog" aria-label={t('bookings.filtersDialogAria')}>
                  <div className="dash-filter-popover-head">
                    <strong>{t('bookings.filterTitle')}</strong>
                    <button type="button" aria-label={t('bookings.closeFilters')} onClick={() => setFilterOpen(false)}><IconX size={15} /></button>
                  </div>
                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('bookings.filterStatus')}</span>
                    <div className="dash-filter-options">
                      {STATUS_FILTERS.map(([value, label, Icon]) => (
                        <button key={value} className={statusFilter === value ? 'active' : ''} type="button" onClick={() => setStatusFilter(value)}>
                          <Icon size={14} />
                          {/* `label` es la CLAVE, no el texto: sin `t()` el menú
                              enseñaba «bookings.filter.all» tal cual. */}
                          <span>{t(label)}</span>
                          {statusFilter === value ? <IconCheck size={14} /> : null}
                        </button>
                      ))}
                    </div>
                  </div>
                  {serviceOptions}
                  {artistOptions}

                  {filtersOn ? (
                    <button
                      className="dash-filter-reset"
                      type="button"
                      onClick={() => {
                        setStatusFilter('all')
                        onServiceFilterChange?.('all')
                      }}
                    >
                      {t('bookings.resetFilters')}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {onCreate ? (
              <button className="dash-primary-btn dash-toolbar-create" type="button" aria-label={t('bookings.new')} onClick={onCreate}>
                <IconPlus size={15} />
                <span className="dash-btn-label dash-btn-label--desktop">{t('bookings.new')}</span>
                <span className="dash-btn-label dash-btn-label--compact">{t('bookings.newShort')}</span>
              </button>
            ) : null}
          </div>
        </header>
      ) : null}

      {failed ? (
        <div className="dash-inline-msg">
          <span>
            {t('bookings.loadFailed')}
            {failureReason ? <code className="dash-failure-detail">{failureReason}</code> : null}
          </span>
          <div className="dash-inline-msg-actions">
            <button type="button" className="dash-secondary-btn" onClick={onReload}>{t('bookings.retry')}</button>
          </div>
        </div>
      ) : services && services.length === 0 && onCreateService ? (
        /*
          Sin un solo servicio no hay cita que pueda existir, así que el vacío
          deja de ofrecer «apuntar una cita» —que aquí no lleva a ninguna parte—
          y ofrece el paso que de verdad falta. Es el mismo bloque que ya enseña
          el calendario: dos pantallas del mismo sitio no pueden contar la misma
          carencia de dos maneras distintas.

          Va ANTES que los demás vacíos a propósito: sin servicios, el filtro y
          el rango dan igual, y «no hay resultados, limpia los filtros» mandaría
          a toquetear unos mandos que no son el problema.

          Sólo con `services` y `onCreateService`: dentro de la ficha de un
          servicio no llega ninguno de los dos, y allí el servicio ya existe.
        */
        <div className="dash-placeholder">
          <IconCalendarPlus size={34} aria-hidden="true" />
          <strong>{t('bookings.cal.noServicesTitle')}</strong>
          <span>{t('bookings.cal.noServicesBody')}</span>
          <button type="button" className="dash-primary-btn" onClick={onCreateService}>
            <IconPlus size={15} aria-hidden="true" /> {t('bookings.cal.noServicesCta')}
          </button>
        </div>
      ) : bookings.length === 0 && !filtersOn && !rangeOn ? (
        <div className="dash-placeholder">
          <IconCalendarEvent size={34} aria-hidden="true" />
          <strong>{t('bookings.empty.title')}</strong>
          <span>{t('bookings.empty.body')}</span>
          {onCreate ? (
            <button type="button" className="dash-primary-btn" onClick={onCreate}>
              <IconPlus size={15} /> {t('bookings.new')}
            </button>
          ) : null}
        </div>
      ) : visible.length === 0 ? (
        <div className="dash-placeholder">
          {/* La lupa y no el calendario: aquí sí hay reservas, lo que no hay es
              ninguna que pase el filtro. El icono lo dice antes que el texto. */}
          <IconSearch size={34} aria-hidden="true" />
          <strong>{t('bookings.noResults.title')}</strong>
          <span>{t('bookings.noResults.body')}</span>
          {/* Limpiar TODO, servicio incluido: vacía por culpa del servicio, la
              lista ofrecía «Nueva cita» y ninguna forma de volver a verlas. */}
          <button
            type="button"
            className="dash-secondary-btn"
            onClick={() => { setQuery(''); setStatusFilter('all'); setArtistFilter('all'); onServiceFilterChange?.('all'); onRangeClear?.() }}
          >
            {t('bookings.clear')}
          </button>
        </div>
      ) : (
        <>
        {/* Encima de la tabla: es donde se decide qué se va a mirar, y debajo
            obligaba a recorrer las diez filas para descubrir que había más. */}
        <Pagination page={paged.page} totalPages={paged.totalPages} total={paged.total} start={paged.start} end={paged.end} onPage={paged.setPage} unit={t('bookings.unit')} />
        <div className="dash-cms-table-scroll">
          <div className={`dash-data-table dash-bookings-data-table${hideService ? ' is-single-service' : ''}`}>
            <div className="dash-data-header" role="row">
              <button className={sort === 'nameAsc' || sort === 'nameDesc' ? 'is-active' : ''} type="button" onClick={() => toggleSort('name')}>
                {t('bookings.col.customer')} <IconChevronDown className={sort === 'nameAsc' ? 'is-asc' : ''} size={13} />
              </button>
              {hideService ? null : <span>{t('bookings.col.service')}</span>}
              {/* Fecha y hora en dos columnas.
                  Iban apiladas en una sola —el día encima y la hora debajo— para
                  ahorrar ancho, pero eso obliga a leer en dos alturas cada fila
                  y la hora, que es lo que se compara entre citas del mismo día,
                  quedaba en letra pequeña. Separadas se recorren en vertical:
                  todas las fechas en una columna y todas las horas en otra. */}
              <button className={sort === 'startSoon' || sort === 'startLate' ? 'is-active' : ''} type="button" onClick={() => toggleSort('start')}>
                {t('bookings.col.date')} <IconChevronDown className={sort === 'startSoon' ? 'is-asc' : ''} size={13} />
              </button>
              <span>{t('bookings.col.time')}</span>
              <span>{t('bookings.col.duration')}</span>
              <span>{t('bookings.col.status')}</span>
            </div>

            <div className="dash-project-list">
              {paged.pageItems.map((entry) => entry.kind === 'group' ? (
                <GroupRow
                  key={entry.key}
                  entry={entry}
                  newArrival={entry.members.some(isNewBooking)}
                  {...(() => {
                    const shot = services?.find((row) => row.id === entry.lead.offerId)?.images[0]
                    return shot ? { image: shot } : {}
                  })()}
                  hideService={hideService}
                  open={openGroup === entry.key}
                  onToggle={() => setOpenGroup((current) => current === entry.key ? null : entry.key)}
                  onOpen={onOpen}
                  onOpenService={onOpenService}
                  busyId={busyId}
                  {...(onStatusChange ? { onStatusChange: changeStatus } : {})}
                />
              ) : (() => {
                const { booking } = entry
                return (
                <article className="dash-project-row dash-data-row dash-loaded-content-in" key={booking.id} onClick={openOnRowClick(() => onOpen(booking.id))}>
                  {/* Sólo el nombre abre la cita, no la fila entera: dentro hay
                      un botón de «Ver» y otro de estado, y una fila pulsable por
                      todas partes convierte cualquier clic fallido en una
                      navegación que nadie pidió. */}
                  <div
                    className="dash-project-row-main"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => { if (event.key === 'Enter') onOpen(booking.id) }}
                  >
                    <span className="dash-project-row-thumb dash-page-project-thumb dash-row-glass-fallback">
                      <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog" aria-hidden="true">
                        <IconUser size={13} />
                      </span>
                    </span>
                    <span className="dash-project-row-copy">
                      <span className="dash-project-row-title">
                        {isNewBooking(booking) ? <NewItemDot /> : null}
                        <strong>{booking.customerName || t('bookings.noName')}{(booking.partySize ?? 1) > 1 ? <small className="dash-booking-party">{t('bookings.partyShort', { count: booking.partySize ?? 1 })}</small> : null}</strong>
                        {/*
                          Repetido junto al nombre para teléfono y tablet, donde
                          no hay encabezados de columna y la columna de estado se
                          esconde. Mismo recurso que usa Ofertas.

                          Y es el MISMO selector, no una etiqueta: siendo un
                          simple texto, por debajo de 1024 el estado dejaba de
                          poderse cambiar —la columna que lo permite está oculta a
                          ese ancho— y el chip que sí se veía no hacía nada al
                          pulsarlo. Sólo uno de los dos está visible a la vez, así
                          que no hay dos mandos compitiendo.
                        */}
                        <span className="dash-booking-status-compact-slot">
                          {onStatusChange ? (
                            <BookingStatusSelect
                              booking={booking}
                              busy={busyId === booking.id}
                              /* En el `body`: la lista está dentro de un
                                 `.dash-cms-table-scroll` con `overflow-x: auto`,
                                 que recorta también por abajo, y el menú de las
                                 últimas filas salía cortado. */
                              portal
                              onChange={(status) => { void changeStatus(booking, status) }}
                            />
                          ) : (
                            <span className={`dash-booking-status ${BOOKING_STATUS_TONE[booking.status]}`}>
                              {t(BOOKING_STATUS_KEY[booking.status])}
                            </span>
                          )}
                        </span>
                      </span>
                      {booking.customerEmail ? <small>{booking.customerEmail}</small> : null}
                      {/* Con quién viene: la cara y el nombre del artista. */}
                      {artistOf(booking) ? (
                        <span className="dash-booking-artist">
                          <ArtistFace name={artistOf(booking)!.name} photoUrl={artistOf(booking)!.photoUrl ?? null} />
                          {t('bookings.withArtist', { name: artistOf(booking)!.name })}
                        </span>
                      ) : null}
                    </span>
                  </div>

                  <div className="dash-list-row-meta dash-booking-row-meta">
                    {hideService ? null : (
                      <span className="dash-data-cell dash-booking-service-cell" data-label={t('bookings.col.service')}>
                        {booking.offerId && onOpenService
                          ? <button type="button" className="dash-booking-service-link" onClick={() => onOpenService(booking.offerId as string)}>{booking.offerTitle || t('bookings.serviceFallback')}</button>
                          : booking.offerTitle || <em>{t('bookings.noService')}</em>}
                      </span>
                    )}
                    {/* El día y la hora juntos: separados en dos columnas
                        obligaban a leer de lado a lado para saber cuándo es. La
                        hora va en la zona del negocio, que viaja en la cita. */}
                    {/* La marca ámbar junto al día: en una lista de veinte
                        filas, lo que se busca es cuáles quedaron sin cerrar, y
                        el estado por sí solo no lo dice —«Confirmada» se lee
                        igual de bien mañana que la semana pasada—. */}
                    <span className="dash-data-cell dash-booking-date-cell" data-label={t('bookings.col.date')}>
                      <BookingOverdueMark booking={booking} />
                      <span className="dash-booking-date-compact">{shortDateIn(booking.startsAt, booking.timezone)}</span>
                      <span className="dash-booking-date-mobile">{mobileDateIn(booking.startsAt, booking.timezone)}</span>
                    </span>
                    <span className="dash-data-cell dash-booking-time-cell" data-label={t('bookings.col.time')}>
                      <IconClock className="dash-booking-time-icon" size={14} aria-hidden="true" />
                      {startTimeLabel(booking, t)}
                    </span>
                    <span className="dash-data-cell dash-booking-duration-cell" data-label={t('bookings.col.duration')}>{durationLabel(booking, t)}</span>
                    <span className="dash-data-cell dash-booking-status-cell" data-label={t('bookings.col.status')}>
                      {/* Selector si se puede cambiar, etiqueta si no. Sin la
                          variante muda el chip parecería pulsable y devolvería
                          403 a quien sólo lleva la agenda. */}
                      {onStatusChange ? (
                        <BookingStatusSelect
                          booking={booking}
                          busy={busyId === booking.id}
                          /* En el `body`, por el mismo recorte del scroll de
                             la lista: ver la fila compacta de arriba. */
                          portal
                          onChange={(status) => { void changeStatus(booking, status) }}
                        />
                      ) : (
                        <span className={`dash-booking-status ${BOOKING_STATUS_TONE[booking.status]}`}>
                          {t(BOOKING_STATUS_KEY[booking.status])}
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="dash-project-row-actions">
                    <button
                      className="dash-secondary-btn dash-project-row-action"
                      type="button"
                      onClick={() => onOpen(booking.id)}
                      aria-label={t('bookings.viewAria', { customer: booking.customerName || t('bookings.unnamedCustomer') })}
                      data-tooltip={t('bookings.viewTooltip')}
                    >
                      <IconEye size={14} />
                      <span className="dash-project-row-action-label">{t('bookings.view')}</span>
                    </button>

                    <div className={`dash-project-row-menu-wrap${menuOpenId === booking.id ? ' is-open' : ''}`}>
                      <button
                        className={`project-menu-btn dash-icon-btn${menuOpenId === booking.id ? ' active' : ''}`}
                        type="button"
                        aria-label={t('bookings.optionsAria', { customer: booking.customerName || t('bookings.unnamedCustomer') })}
                        aria-expanded={menuOpenId === booking.id}
                        onClick={(event) => {
                          if (menuOpenId !== booking.id) {
                            /* Medidas fijas y no medidas del DOM: preguntarle
                               obligaría a pintar el menú antes de colocarlo, y
                               eso se ve como un salto. Mismo cálculo que en
                               Servicios y en Páginas. */
                            const rect = event.currentTarget.getBoundingClientRect()
                            const menuWidth = 184
                            const menuHeight = 152
                            const edge = 12
                            const gap = 8
                            const spaceBelow = window.innerHeight - rect.bottom - edge
                            const spaceAbove = rect.top - edge
                            const up = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow
                            const top = up
                              ? Math.max(edge, rect.top - menuHeight - gap)
                              : Math.min(window.innerHeight - menuHeight - edge, rect.bottom + gap)
                            const left = Math.max(edge, Math.min(window.innerWidth - menuWidth - edge, rect.right - menuWidth))
                            setListMenu({ top, left, up, maxHeight: Math.max(menuHeight, up ? spaceAbove - gap : spaceBelow - gap) })
                          }
                          setMenuOpenId((id) => (id === booking.id ? null : booking.id))
                        }}
                      >
                        <IconDots size={16} />
                      </button>

                      {menuOpenId === booking.id ? createPortal(
                        <>
                          <div className="project-menu-backdrop" role="presentation" onClick={() => setMenuOpenId(null)} />
                          <div
                            className={`project-menu dash-project-row-menu dash-svc-row-menu-portal${listMenu.up ? ' is-up' : ''}`}
                            role="menu"
                            style={{ top: listMenu.top, left: listMenu.left, maxHeight: listMenu.maxHeight, overflowY: 'auto' }}
                          >
                            {/* Escribir y llamar sólo si hay a dónde: una cita
                                puede llegar sin correo o sin teléfono, y un
                                «mailto:» vacío abre el programa de correo con la
                                nada dentro. */}
                            {booking.customerEmail ? (
                              <a className="project-menu-item" role="menuitem" href={`mailto:${booking.customerEmail}`} onClick={() => setMenuOpenId(null)}>
                                <IconMail size={14} /> {t('bookings.detail.emailCustomer')}
                              </a>
                            ) : null}
                            {booking.customerPhone ? (
                              <a className="project-menu-item" role="menuitem" href={`tel:${booking.customerPhone}`} onClick={() => setMenuOpenId(null)}>
                                <IconPhone size={14} /> {t('bookings.detail.call')}
                              </a>
                            ) : null}
                            {booking.customerEmail ? (
                              <button
                                className="project-menu-item"
                                type="button"
                                onClick={() => {
                                  void navigator.clipboard?.writeText(booking.customerEmail)
                                  setCopiedId(booking.id)
                                  window.setTimeout(() => setCopiedId(null), 1400)
                                }}
                              >
                                {copiedId === booking.id ? <IconCheck size={14} /> : <IconCopy size={14} />}
                                {' '}{t(copiedId === booking.id ? 'bookings.detail.copied' : 'bookings.detail.copyEmail')}
                              </button>
                            ) : null}
                            {booking.customerEmail || booking.customerPhone ? <div className="project-menu-rule" /> : null}
                            <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); onOpen(booking.id) }}>
                              <IconEye size={14} /> {t('bookings.view')}
                            </button>
                            {booking.offerId && onOpenService ? (
                              <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); onOpenService(booking.offerId as string) }}>
                                <IconCalendarEvent size={14} /> {t('bookings.detail.viewService')}
                              </button>
                            ) : null}
                          </div>
                        </>,
                        document.body,
                      ) : null}
                    </div>
                  </div>
                </article>
                )
              })())}
            </div>
          </div>
        </div>
        {/* Y otra vez al pie: quien acaba de recorrer la página busca el
            siguiente paso donde terminó de leer, no volviendo arriba. Con una
            sola página no se repite el mando —sólo queda la cuenta—. */}
        <Pagination page={paged.page} totalPages={paged.totalPages} total={paged.total} start={paged.start} end={paged.end} onPage={paged.setPage} unit={t('bookings.unit')} alwaysShow />
        </>
      )}

    </>
  )
}
